import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { engageApi, leadApi, escalationApi, responsibilityApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Target, MessageSquare, Clock, AlertTriangle, Users2, CheckCircle2,
  ArrowRight, Inbox, PlusCircle, TrendingUp, Zap,
  Shield, UserCheck, ChevronRight, Activity,
} from "lucide-react";
import { ENGAGE } from "@/constants/testIds";
import { leadStatusLabel, qualStatusLabel } from "../utils/leadStatusLabels";

const STATUS_COLOR = {
  NEW: "text-sky-400 bg-sky-400/10",
  CONTACTED: "text-[#0176D3] bg-blue-400/10",
  QUALIFYING: "text-indigo-400 bg-indigo-400/10",
  QUALIFIED: "text-[#2E844A] bg-emerald-400/10",
  NURTURE: "text-[#8C4B00] bg-amber-400/10",
  POC_REVIEW: "text-rose-400 bg-rose-400/10",
  CONVERTED: "text-[#2E844A] bg-teal-400/10",
  DISQUALIFIED: "text-[#706E6B] bg-slate-500/10",
  CLOSED: "text-[#706E6B] bg-[#E5E5E5]/10",
};

const QUAL_COLOR = {
  QUALIFIED: "text-[#2E844A]",
  PARTIALLY_QUALIFIED: "text-[#8C4B00]",
  UNQUALIFIED: "text-[#706E6B]",
  POC_REVIEW_REQUIRED: "text-rose-400",
};

export default function EngageDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats,      setStats]      = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [myActions,  setMyActions]  = useState(null);
  const [loading,    setLoading]    = useState(true);

  const isSupervisor = ["BUSINESS_OWNER", "CRM_MANAGER", "TENANT_ADMIN", "PLATFORM_ADMIN"].includes(user?.role);

  useEffect(() => {
    Promise.all([
      engageApi.getDashboard(),
      escalationApi.list({ status: "OPEN" }),
      responsibilityApi.getMyActions(8).catch(() => null),
    ]).then(([s, e, ma]) => {
      setStats(s.data);
      setEscalations(Array.isArray(e.data) ? e.data.slice(0, 3) : []);
      setMyActions(ma?.data || null);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;

  const statCards = [
    { label: "Total Leads", value: stats?.total_leads ?? 0, icon: Target, color: "text-[#0176D3]", bg: "bg-[#EEF4FF]" },
    { label: "New Inquiries", value: stats?.new_inquiries ?? 0, icon: Inbox, color: "text-[#2E844A]", bg: "bg-emerald-400/10" },
    { label: "Qualified", value: stats?.qualified_leads ?? 0, icon: CheckCircle2, color: "text-[#2E844A]", bg: "bg-teal-400/10" },
    { label: "Planning Ready", value: stats?.planning_ready ?? stats?.poc_review_required ?? 0, icon: AlertTriangle, color: "text-rose-400", bg: "bg-rose-400/10" },
    { label: "Unassigned", value: stats?.unassigned_leads ?? 0, icon: Users2, color: "text-[#8C4B00]", bg: "bg-amber-400/10" },
    { label: "Overdue Follow-ups", value: stats?.overdue_followups ?? 0, icon: Clock, color: "text-[#8C4B00]", bg: "bg-orange-400/10" },
    { label: "Open Escalations", value: stats?.open_escalations ?? 0, icon: Zap, color: "text-[#706E6B]", bg: "bg-[#F3F3F3]" },
    { label: "Recent Activity", value: (stats?.recent_leads ?? []).length, icon: TrendingUp, color: "text-indigo-400", bg: "bg-indigo-400/10" },
  ];

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid={ENGAGE.dashboard}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#181818] font-sans">ONE Engage</h1>
          <p className="text-sm text-[#706E6B] mt-1">Lead Operations · Inquiry Intelligence · Agent Runtime</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-[#706E6B]">{user?.tenant_name}</span>
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid={ENGAGE.statsCard}>
        {statCards.map(s => (
          <div key={s.label} className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4 hover:border-[#0176D3]/30 transition-colors cursor-pointer"
            onClick={() => {
              if (s.label === "New Inquiries") navigate("/engage/inbox");
              else if (s.label.includes("Follow")) navigate("/engage/follow-ups");
              else navigate("/engage/leads");
            }}>
            <div className={`w-8 h-8 rounded-lg ${s.bg} flex items-center justify-center mb-3`}>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <p className="text-2xl font-bold text-[#181818] font-sans">{s.value}</p>
            <p className="text-xs text-[#706E6B] mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3 flex-wrap" data-testid={ENGAGE.quickActions}>
        <button
          onClick={() => navigate("/engage/inbox")}
          className="flex items-center gap-2 px-4 py-2 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg text-sm text-[#0176D3] hover:border-[#0176D3]/30 transition-all font-sans">
          <Inbox className="w-4 h-4" /> View Inbox
        </button>
        <button
          onClick={() => navigate("/engage/leads")}
          className="flex items-center gap-2 px-4 py-2 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg text-sm text-[#444444] hover:text-[#0176D3] hover:border-[#0176D3]/30 transition-all font-sans">
          <Target className="w-4 h-4" /> All Leads
        </button>
        <button
          onClick={() => navigate("/engage/follow-ups")}
          className="flex items-center gap-2 px-4 py-2 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg text-sm text-[#444444] hover:text-[#8C4B00] hover:border-amber-400/50 transition-all font-sans">
          <Clock className="w-4 h-4" /> Follow-ups
        </button>
        <button
          onClick={() => navigate("/engage/inbox")}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-sm text-[#2E844A] hover:bg-emerald-500/20 transition-all font-sans">
          <PlusCircle className="w-4 h-4" /> New Inquiry
        </button>
      </div>

      {/* CRM-UX-03: My Actions Section */}
      {myActions && (
        <MyActionsSection
          myActions={myActions}
          isSupervisor={isSupervisor}
          user={user}
          navigate={navigate}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Leads */}
        <div className="lg:col-span-2 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5" data-testid={ENGAGE.recentLeads}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-[#0176D3]" />
              <h2 className="text-sm font-semibold text-[#181818] font-sans">Recent Leads</h2>
            </div>
            <button onClick={() => navigate("/engage/leads")}
              className="flex items-center gap-1 text-xs text-[#0176D3] hover:text-sky-300 transition-colors">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {(stats?.recent_leads ?? []).length === 0 ? (
            <p className="text-sm text-[#706E6B] py-4 text-center">No leads yet — start by processing an inquiry</p>
          ) : (
            <div className="space-y-2">
              {(stats?.recent_leads ?? []).map(lead => (
                <div key={lead.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-white hover:bg-[#EEF4FF] cursor-pointer transition-colors border border-transparent hover:border-[#0176D3]/20"
                  onClick={() => navigate(`/engage/leads/${lead.id}`)}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-mono text-[#0176D3] flex-shrink-0">{lead.lead_number}</span>
                    <div className="min-w-0">
                      <p className="text-sm text-[#181818] truncate font-sans">
                        {lead.customer_name || lead.customer_id}
                      </p>
                      <p className="text-xs text-[#706E6B] truncate">{lead.event_type_interest || "—"} · {lead.source_channel}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${STATUS_COLOR[lead.status] || "text-[#706E6B] bg-slate-500/10"}`}>
                      {leadStatusLabel(lead.status)}
                    </span>
                    <ArrowRight className="w-3 h-3 text-[#706E6B]" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Open Escalations */}
        <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-rose-400" />
            <h2 className="text-sm font-semibold text-[#181818] font-sans">Open Escalations</h2>
            {escalations.length > 0 && (
              <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400">
                {escalations.length}
              </span>
            )}
          </div>
          {escalations.length === 0 ? (
            <p className="text-sm text-[#706E6B] py-4 text-center">No open escalations</p>
          ) : (
            <div className="space-y-3">
              {escalations.map(esc => (
                <div key={esc.id}
                  className="p-3 rounded-lg bg-rose-500/5 border border-rose-500/20 cursor-pointer hover:bg-rose-500/10 transition-colors"
                  onClick={() => navigate(`/engage/leads/${esc.lead_id}`)}>
                  <p className="text-xs font-mono text-rose-400 mb-1">{esc.priority} PRIORITY</p>
                  <p className="text-xs text-[#444444] line-clamp-2">{esc.reason}</p>
                  <p className="text-[10px] text-[#706E6B] mt-1">{formatRelativeTime(esc.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatRelativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── CRM-UX-03: My Actions Section ─────────────────────────────────────────────
function MyActionsSection({ myActions, isSupervisor, user, navigate }) {
  const isBO = user?.role === "BUSINESS_OWNER";
  const leads = myActions?.my_leads || [];
  const opps  = myActions?.my_opps  || [];
  const tp    = myActions?.team_pipeline;
  const overdueCount = myActions?.overdue_followups || 0;

  // Nothing to show
  if (leads.length === 0 && opps.length === 0 && !tp && overdueCount === 0) return null;

  const STATUS_DOT = {
    NEW: "bg-sky-400",
    CONTACTED: "bg-[#0176D3]",
    QUALIFYING: "bg-indigo-400",
    QUALIFIED: "bg-[#2E844A]",
    NURTURE: "bg-amber-400",
    POC_REVIEW: "bg-rose-400",
  };

  return (
    <div className="space-y-3" data-testid="my-actions-section">
      {/* Section header */}
      <div className="flex items-center gap-2">
        {isBO
          ? <Shield className="w-4 h-4 text-[#92400E]" />
          : <UserCheck className="w-4 h-4 text-[#0176D3]" />}
        <h2 className="text-sm font-semibold text-[#181818] font-sans">
          {isBO ? "Business Owner Actions" : "My Actions"}
        </h2>
        {overdueCount > 0 && (
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">
            {overdueCount} overdue
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* My Leads */}
        {leads.length > 0 && (
          <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4" data-testid="my-actions-leads">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="w-3.5 h-3.5 text-[#0176D3]" />
                <span className="text-xs font-semibold text-[#181818] font-sans">
                  {isBO ? "Personal Leads" : "My Leads"}
                </span>
                <span className="text-[10px] font-mono px-1 py-0.5 rounded bg-[#EEF4FF] text-[#0176D3]">{leads.length}</span>
              </div>
              <button
                onClick={() => navigate("/engage/leads?scope=my")}
                className="text-[10px] text-[#0176D3] hover:text-sky-300 flex items-center gap-0.5">
                View All <ArrowRight className="w-2.5 h-2.5" />
              </button>
            </div>
            <div className="space-y-1.5">
              {leads.slice(0, 5).map(lead => (
                <div
                  key={lead.id}
                  data-testid={`my-action-lead-${lead.id}`}
                  className="flex items-center gap-2 p-2 rounded-lg bg-white hover:bg-[#EEF4FF] cursor-pointer transition-colors border border-transparent hover:border-[#0176D3]/20"
                  onClick={() => navigate(lead.route)}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_DOT[lead.status] || "bg-[#C9C9C9]"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-[#181818] truncate font-sans">{lead.name}</p>
                    <p className="text-[10px] text-[#706E6B]">{leadStatusLabel(lead.status)} {lead.event_type ? `· ${lead.event_type}` : ""}</p>
                  </div>
                  <ChevronRight className="w-3 h-3 text-[#C9C9C9] flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My Opportunities */}
        {opps.length > 0 && (
          <div className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-4" data-testid="my-actions-opps">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-indigo-400" />
                <span className="text-xs font-semibold text-[#181818] font-sans">
                  {isBO ? "Personal Opportunities" : "My Opportunities"}
                </span>
                <span className="text-[10px] font-mono px-1 py-0.5 rounded bg-indigo-400/10 text-indigo-400">{opps.length}</span>
              </div>
              <button
                onClick={() => navigate("/crm/opportunities?scope=my")}
                className="text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-0.5">
                View All <ArrowRight className="w-2.5 h-2.5" />
              </button>
            </div>
            <div className="space-y-1.5">
              {opps.slice(0, 5).map(opp => (
                <div
                  key={opp.id}
                  data-testid={`my-action-opp-${opp.id}`}
                  className="flex items-center gap-2 p-2 rounded-lg bg-white hover:bg-indigo-400/5 cursor-pointer transition-colors border border-transparent hover:border-indigo-400/20"
                  onClick={() => navigate(opp.route)}
                >
                  <Activity className="w-2.5 h-2.5 text-indigo-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-[#181818] truncate font-sans">{opp.name}</p>
                    <p className="text-[10px] text-[#706E6B]">{opp.stage?.replace(/_/g, " ")}</p>
                  </div>
                  <ChevronRight className="w-3 h-3 text-[#C9C9C9] flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Pipeline (BO/CRM_MANAGER only) */}
        {tp && isSupervisor && (
          <div className="lg:col-span-2 bg-[#FEF3C7] border border-[#FCD34D] rounded-xl p-4" data-testid="team-pipeline-panel">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-3.5 h-3.5 text-[#92400E]" />
              <span className="text-xs font-semibold text-[#92400E] font-sans">
                Tenant Pipeline Supervision
              </span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {[
                { label: "Total Leads",      value: tp.total_leads,      color: "text-[#181818]" },
                { label: "Unassigned",        value: tp.unassigned_leads, color: "text-[#8C4B00]" },
                { label: "Actionable Leads",  value: tp.actionable_leads, color: "text-[#0176D3]" },
                { label: "Total Opps",        value: tp.total_opps,       color: "text-indigo-600" },
                { label: "Active Opps",       value: tp.active_opps,      color: "text-indigo-600" },
                { label: "Overdue Follow-ups",value: tp.all_overdue,      color: "text-rose-600" },
              ].map(({ label, value, color }) => (
                <div key={label} className="text-center">
                  <p className={`text-xl font-bold font-sans ${color}`}>{value}</p>
                  <p className="text-[10px] text-[#92400E] leading-tight">{label}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <button
                onClick={() => navigate("/engage/leads")}
                className="text-[10px] px-2.5 py-1 bg-[#92400E]/10 text-[#92400E] rounded-lg border border-[#92400E]/20 hover:bg-[#92400E]/20 transition-colors">
                View All Leads
              </button>
              <button
                onClick={() => navigate("/crm/opportunities")}
                className="text-[10px] px-2.5 py-1 bg-[#92400E]/10 text-[#92400E] rounded-lg border border-[#92400E]/20 hover:bg-[#92400E]/20 transition-colors">
                View All Opportunities
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="h-8 w-48 bg-[#F3F3F3] rounded animate-pulse" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-[#F3F3F3] rounded-xl h-24 animate-pulse" />
        ))}
      </div>
    </div>
  );
}
